/**
 * AssumptionsBlock - Display assumptions with scenario grouping
 */

import React from 'react'
import { AssumptionsBlock as AssumptionsBlockType } from '../../../../lib/cockpit/schemas'
import { useScenario } from '../../scenario/ScenarioContext'

interface AssumptionsBlockProps {
  data: AssumptionsBlockType['data']
  blockId: string
  compact?: boolean
}

const TYPE_ICONS = {
  scenario: '🎯',
  model: '📐',
  definition: '📖',
  constraint: '⚠️',
}

function AssumptionsBlock({ data, blockId, compact }: AssumptionsBlockProps) {
  const { axis, activeState, getStateLabel } = useScenario()

  // Group by state if scenario grouping enabled
  const grouped = data.show_scenario_grouping && axis
    ? data.assumptions.reduce((acc: Record<string, any[]>, assumption: any) => {
        const stateId = assumption.applies_to_state || 'all'
        if (!acc[stateId]) acc[stateId] = []
        acc[stateId].push(assumption)
        return acc
      }, {} as Record<string, any[]>)
    : { all: data.assumptions }

  if (compact) {
    return (
      <div className="space-y-1">
        {data.assumptions.slice(0, 4).map((assumption: any) => (
          <div key={assumption.id} className="flex items-start gap-2 text-sm">
            <span>{TYPE_ICONS[(assumption.type || 'model') as keyof typeof TYPE_ICONS]}</span>
            <span className="text-gray-700 dark:text-gray-300 line-clamp-1">{assumption.text}</span>
          </div>
        ))}
        {data.assumptions.length > 4 && (
          <div className="text-xs text-gray-500">+{data.assumptions.length - 4} more</div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {Object.entries(grouped).map(([stateId, assumptions]) => (
        <div key={stateId}>
          {data.show_scenario_grouping && stateId !== 'all' && (
            <h4 className={`
              text-sm font-medium mb-2 px-2 py-1 rounded
              ${stateId === activeState 
                ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200' 
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
              }
            `}>
              {stateId === 'all' ? 'All Scenarios' : getStateLabel(stateId) || stateId}
            </h4>
          )}
          
          <div className="space-y-2">
            {(assumptions as any[]).map((assumption: any) => (
              <div
                key={assumption.id}
                className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg"
              >
                <span className="text-lg">{TYPE_ICONS[(assumption.type || 'model') as keyof typeof TYPE_ICONS]}</span>
                <div className="flex-1">
                  <p className="text-gray-800 dark:text-gray-200 text-sm">
                    {assumption.text}
                  </p>
                  {assumption.type && (
                    <span className="inline-block mt-1 px-2 py-0.5 text-xs rounded bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                      {assumption.type}
                    </span>
                  )}
                  {assumption.evidence_anchors && assumption.evidence_anchors.length > 0 && (
                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-500">
                      {assumption.evidence_anchors.length} source{assumption.evidence_anchors.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

export default AssumptionsBlock



